import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Zap, Feather } from "lucide-react";
import { Logo } from "./Logo";
import { NAV } from "@/lib/constants";
import { useMode } from "@/context/ModeContext";

export function ModeToggle({ testid = "mode-toggle" }) {
  const { mode, setMode } = useMode();
  const premium = mode === "premium";
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => setMode(premium ? "light" : "premium")}
      aria-pressed={premium}
      title={premium ? "Passa alla versione Light (senza effetti)" : "Passa alla versione Premium (con effetti 3D)"}
      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--argento)] transition-colors hover:border-[var(--blu)] hover:text-white"
    >
      {premium ? <Zap size={14} className="text-[var(--rosso)]" /> : <Feather size={14} className="text-[var(--blu)]" />}
      {premium ? "Premium" : "Light"}
    </button>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const linkCls = ({ isActive }) =>
    `relative px-1 py-2 text-sm font-medium transition-colors ${isActive ? "text-white" : "text-[var(--argento)] hover:text-white"} after:absolute after:-bottom-0.5 after:left-0 after:h-[2px] after:bg-gradient-to-r after:from-[var(--rosso)] after:to-[var(--blu)] after:transition-all ${isActive ? "after:w-full" : "after:w-0 hover:after:w-full"}`;

  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${scrolled || open ? "glass" : "bg-transparent"}`}>
      <div className="wrap flex h-[72px] items-center justify-between gap-4">
        <Logo />
        <nav aria-label="Menu principale" className="hidden items-center gap-7 lg:flex">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}>{n.label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="hidden sm:inline-flex"><ModeToggle /></span>
          <Link to="/segnala-cosa-cerchi" data-testid="header-cta-segnala" className="btn btn-red hidden px-5 py-2.5 md:inline-flex">Segnala cosa cerchi</Link>
          <button type="button" data-testid="mobile-menu-toggle" aria-expanded={open} aria-label={open ? "Chiudi menu" : "Apri menu"} onClick={() => setOpen(!open)} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 lg:hidden">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <nav aria-label="Menu mobile" data-testid="mobile-menu" className="wrap flex flex-col gap-1 pb-6 lg:hidden">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`mobile-nav-${n.to === "/" ? "home" : n.to.slice(1)}`} className={({ isActive }) => `rounded-xl px-4 py-3 text-base font-medium ${isActive ? "bg-white/5 text-white" : "text-[var(--argento)]"}`}>{n.label}</NavLink>
          ))}
          <div className="mt-3 flex items-center gap-3 px-1">
            <Link to="/segnala-cosa-cerchi" data-testid="mobile-cta-segnala" className="btn btn-red flex-1">Segnala cosa cerchi</Link>
            <ModeToggle testid="mobile-mode-toggle" />
          </div>
        </nav>
      )}
    </header>
  );
}
